/* eslint-disable @typescript-eslint/no-unused-vars */
import { AutocompleteWithLabel, InputWithLabel } from '@adotel/components'
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { matchSorter } from 'match-sorter'
import { useEffect, useState } from 'react'

import { getTranslate } from '../../localization'
import { EntityList } from '../../types/entityList'
import {
  AssignmentType,
  BulkUpdateFeatureFlagInput,
  RoleFeature
} from '../../types/featureFlag'
import { EntityType } from '../../types/reserveMonitoringList'
import { RoleBrief } from '../../types/role'
import useStyles from './styles/filters.styles'

/**
 * One option in the entity picker.
 *
 * Typed rather than `any[]` because `any` is what let the bug compile: the
 * component's own .d.ts declares its props as Multiple=true regardless of the
 * runtime prop, so `values: any[]` type-checked whether or not `multiple` was
 * passed. TypeScript was validating against a declaration, and the declaration
 * against nothing.
 */
type EntityOption = { label: string; value: string }

type Props = {
  autoCompleteForRole: any
  setAutoCompleteForRele: React.Dispatch<any>
  autoCompleteForEntity: any
  setAutoCompleteForEntity: React.Dispatch<any>
  bulkUpdateFeatureFlagInput: BulkUpdateFeatureFlagInput
  setBulkUpdateFeatureFlagInput: React.Dispatch<
    React.SetStateAction<BulkUpdateFeatureFlagInput>
  >
  roles: RoleBrief[]
  entityList: EntityList[]
  initialFeatures: RoleFeature[]
  setFilteredFeatures: React.Dispatch<React.SetStateAction<RoleFeature[]>>
}

export default function FeatureFilters({
  autoCompleteForRole,
  setAutoCompleteForRele,
  autoCompleteForEntity,
  setAutoCompleteForEntity,
  bulkUpdateFeatureFlagInput,
  setBulkUpdateFeatureFlagInput,
  roles,
  entityList,
  initialFeatures,
  setFilteredFeatures
}: Props) {
  const classes = useStyles()
  const [searchInputVal, setSearchInputVal] = useState('')

  useEffect(() => {
    let searchfilteredFeatures = searchInputVal
      ? matchSorter(initialFeatures, searchInputVal, {
          keys: ['featureGroup', 'features.*.name']
        })
      : initialFeatures

    setFilteredFeatures(searchfilteredFeatures)
  }, [searchInputVal, initialFeatures])

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <AutocompleteWithLabel
          options={
            roles.length > 0
              ? roles.map((role) => ({
                  label: role.name,
                  id: role.roleID,
                  value: (role.entityType as EntityType) || ''
                }))
              : []
          }
          inputSize="small"
          getOptionLabel={(option) => option.label || ''}
          value={autoCompleteForRole}
          onChange={(_, value: any) => {
            if (value) {
              setAutoCompleteForRele({
                value: value.value,
                id: value.id,
                label: value.label
              })
              setBulkUpdateFeatureFlagInput({
                ...bulkUpdateFeatureFlagInput,
                roleID: value.id,
                featureIDs: []
              })
            } else {
              setAutoCompleteForRele(null)
              setAutoCompleteForEntity(null)
              setBulkUpdateFeatureFlagInput({
                roleID: '',
                featureIDs: [],
                entityCodes: [],
                assignmentType: 'Assign'
              })
            }
          }}
          label={getTranslate('Role')}
        />
        {autoCompleteForRole && autoCompleteForRole.value && (
          <InputWithLabel
            label={getTranslate('Entity Type')}
            fullWidth
            inputSize="small"
            value={(autoCompleteForRole && autoCompleteForRole.value) || ''}
            disabled
          />
        )}
        {autoCompleteForRole && autoCompleteForRole.value && (
          <AutocompleteWithLabel
            options={
              entityList
                ? entityList.map((entity) => ({
                    label: entity.label,
                    value: entity.entityCode
                  }))
                : []
            }
            /* Load-bearing, and its absence made this control inert.

               AutocompleteWithLabel branches internally:

                 onChange: multiple ? handleChange : onChange
                 value:    multiple ? selectedItems : value

               Without it the handler below is handed to MUI's SINGLE-select
               Autocomplete, which invokes it with one option object. That object
               has no `.length`, so `values.length > 0` is `undefined > 0` —
               always false — and every pick fell through to the else branch:
               entityCodes was never set and the selection was cleared.

               It failed SILENTLY, unlike the same omission in TicketWatchers
               (admin-panel !1229), where the handler called `.forEach` and threw.
               A crash gets reported; a control that quietly does nothing gets
               lived with.

               The state shape already expects multi-select — the handler maps
               over `values` and stores the array — so this restores the intent
               rather than changing it. `autoCompleteForEntity` starts as null,
               which the component reads as falsy and treats as an empty
               selection, so no state change is needed alongside. */
            multiple
            inputSize="small"
            getOptionLabel={(option) => option.label || ''}
            value={autoCompleteForEntity}
            onChange={(_, values: EntityOption[]) => {
              if (values && values.length > 0) {
                const entityCodes = values.map((v) => v.value)
                setAutoCompleteForEntity(values)
                if (bulkUpdateFeatureFlagInput.roleID) {
                  setBulkUpdateFeatureFlagInput({
                    ...bulkUpdateFeatureFlagInput,
                    entityCodes: entityCodes
                  })
                }
              } else {
                setAutoCompleteForEntity(null)
              }
            }}
            label={getTranslate('Entities')}
          />
        )}
      </div>
      <div className={classes.row2}>
        <InputWithLabel
          label={getTranslate('Features')}
          placeholder={getTranslate('Search in Features')}
          value={searchInputVal}
          onChange={(event) => setSearchInputVal(event.currentTarget.value)}
          autoComplete="off"
          noHelperText
          fullWidth
          startIcon={<SearchIcon />}
          inputSize="small"
        />
      </div>
      <div className={classes.methodSelector}>
        <Typography className={classes.methodTitle}>
          {getTranslate('Method Selector')}
        </Typography>
        <div className={classes.radioGroup}>
          <RadioGroup
            row
            value={bulkUpdateFeatureFlagInput.assignmentType}
            onChange={(e) =>
              setBulkUpdateFeatureFlagInput((prev) => ({
                ...prev,
                assignmentType: e.target.value as AssignmentType
              }))
            }>
            <FormControlLabel
              value="Assign"
              control={<Radio color="primary" size="small" />}
              label={getTranslate('Add')}
            />
            <FormControlLabel
              value="Revoke"
              control={<Radio color="primary" size="small" />}
              label={getTranslate('Remove')}
            />
          </RadioGroup>
        </div>
      </div>
    </div>
  )
}
